import cn from 'classnames';
import React, { useState } from 'react';

import Check from '@sats-group/icons/16/check';
import Info from '@sats-group/icons/16/info';
import Tag from '@sats-group/ui-lib/react/tag/tag';
import Text from '@sats-group/ui-lib/react/text/text';

import Spinner from '../spinner/spinner';

import type {
  OptionLink,
  OptionRadio,
  Options as Props,
} from './options.props';

const Content: React.FunctionComponent<
  (OptionLink | OptionRadio) & { isChecked?: boolean }
> = ({ availableCampaign, isChecked, priceDifference, title }) => (
  <React.Fragment>
    <div
      className={cn('options__check', {
        'options__check--checked': isChecked,
      })}
    >
      <Check />
    </div>
    <Text>{title}</Text>
    {priceDifference || availableCampaign ? (
      <div className="options__tags">
        {priceDifference ? (
          <Tag
            variant={
              priceDifference.text.startsWith('−')
                ? Tag.variants.positive
                : Tag.variants.secondary
            }
            size={Tag.sizes.xs}
            {...priceDifference}
          />
        ) : null}
        {availableCampaign ? (
          <Tag
            variant={Tag.variants.positive}
            size={Tag.sizes.xs}
            {...availableCampaign}
          />
        ) : null}
      </div>
    ) : null}
  </React.Fragment>
);

const Options: React.FunctionComponent<Props> = props => {
  const [loading, setLoading] = useState<boolean>(false);

  if (props.type === 'radio') {
    return (
      <div className="options">
        {props.options.map(option => (
          <div className="options__item" key={option.value}>
            <label className="options__option options__option--interactive">
              <input
                className="options__radio"
                defaultChecked={option.defaultChecked}
                name={props.name}
                required={props.required}
                type="radio"
                value={option.value}
              />
              <Content {...option} />
            </label>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="options">
      {loading ? <Spinner theme={Spinner.themes.overlay} /> : null}
      {props.options.map(option => (
        <React.Fragment key={option.title}>
          <div className="options__item">
            {option.isSelected ? (
              <div aria-label={option.ariaLabel} className="options__option">
                <Content {...option} isChecked />
              </div>
            ) : (
              <a
                aria-label={option.ariaLabel}
                className="options__option options__option--interactive"
                href={option.link}
                onClick={() => {
                  option.onClick?.();
                  setLoading(true);
                }}
              >
                <Content {...option} />
              </a>
            )}
          </div>
          {option.disclaimer && option.isSelected ? (
            <div className="options__disclaimer">
              <Info />
              <Text size={Text.sizes.interface}>{option.disclaimer}</Text>
            </div>
          ) : null}
        </React.Fragment>
      ))}
    </div>
  );
};

export default Options;
